/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */
import IScene from "../scene_interface";
import TerrainTileAddress from "./terrain_tile_address";
import TerrainTile from "./terrain_tile";
import HeightMapTile from "../elements/height_map_tile";
import WGS84Projection from "./wgs84_projection";
import ITerrain from "./terrain_interface";

export type TLatLng = {lat: number, lng: number};

export default 
class Terrain implements ITerrain {

    /***
     * The Bing texture tile size, do not change please, in future, the Bing or other module should define this size
     * @type {{width: number, height: number}}
     */
    public static TEXTURE_SIZE: { width: number, height: number } = {
        width: 256,
        height: 256
    };

    private _sceneAPI: IScene;
    private _root: TerrainTile;
    private center: TLatLng | null;
    private xOffset: number;
    private yOffset: number;
    private heightOffset: number;
    private heightMapTiles: HeightMapTile[];

    constructor(aSceneAPI: IScene) {
        this._sceneAPI = aSceneAPI;

        let rootTileAddress = new TerrainTileAddress(0, 0, 0);
        this._root = new TerrainTile(this, rootTileAddress);

        this.center = null;

        this.xOffset = 0;
        this.yOffset = 0;
        this.heightOffset = 0;

        this.heightMapTiles = [];
    }

    private _findHighestUsedDataTileLevel(aConcentricSelections: any[]): number {
        let topZoomLevel = 0;
        let lowZoomLevel = 21;
        for (let key in aConcentricSelections) {
            let selection = aConcentricSelections[key];
            for (let j = 0; j < selection.tiles.length; j++) {
                let dataTile = selection.tiles[j];
                if (dataTile.level.zoomLevel > topZoomLevel) {
                    topZoomLevel = dataTile.level.zoomLevel;
                }
                if (dataTile.level.zoomLevel < lowZoomLevel) {
                    lowZoomLevel = dataTile.level.zoomLevel;
                }
            }
        }

        return topZoomLevel;
    }

    public getRoot(): TerrainTile {
        return this._root;
    }

    public create(aConcentricSelections: any[]) {
        let levels = {};
        let descOrderedLevelsValues = [];
        for (let i = 0; i < aConcentricSelections.length; i++) {
            levels[aConcentricSelections[i].level] = aConcentricSelections[i];
            descOrderedLevelsValues.push(aConcentricSelections[i].level);
        }

        descOrderedLevelsValues.sort(function (a, b) {
            return a - b;
        });

        // get top zoom level used within the data tiles
        let topZoomLevel = this._findHighestUsedDataTileLevel(aConcentricSelections);

        this.heightMapTiles = [];
        for (let i = 0; i < descOrderedLevelsValues.length; i++) {
            let selection = levels[descOrderedLevelsValues[i]];
            for (let j = 0; j < selection.tiles.length; j++) {
                let dataTile = selection.tiles[j];
                dataTile.level.topZoomLevel = topZoomLevel;
                let addedHeightmapTile = this._addDataTile(dataTile);
                /*if (i > 0) {
                    addedHeightmapTile.subSample({
                        cols: 9,
                        rows: 9
                    });
                }*/
            }
        }

        this.createReferencesToHeightMapTiles();
        this.completeChildren();
        this.stitchTilesByChangingElevation();
        this.computeTesselatedAreas();

        // compute center of the concentric selectios
        this.center = {
            lat: aConcentricSelections[0].corners.sw.lat + (aConcentricSelections[0].corners.ne.lat - aConcentricSelections[0].corners.sw.lat) / 2,
            lng: aConcentricSelections[0].corners.sw.lng + (aConcentricSelections[0].corners.ne.lng - aConcentricSelections[0].corners.sw.lng) / 2
        };
    }

    private _addDataTile(aDataTile: any) {
        let heightMapTile = <HeightMapTile> this._sceneAPI.getDocument().createAPIElement(HeightMapTile.TypeName);
        heightMapTile.setTerrain(this);
        heightMapTile.setDataTile(aDataTile);
        this.heightMapTiles.push(heightMapTile);

        this._root.addDataTile(aDataTile, heightMapTile);

        return heightMapTile;
    }

    public getRootTile(): TerrainTile {
        return this._root;
    }

    public getCenter(): TLatLng {
        return this.center;
    }

    public setHeightOffset(aHeightOffset: number) {
        this.heightOffset = aHeightOffset;
    }

    public getHeightOffset(): number {
        return this.heightOffset;
    }

    public setXYOffset(aXOffset: number, aYOffset: number) {
        this.xOffset = aXOffset;
        this.yOffset = aYOffset;
    }

    public getXOffset(): number {
        return this.xOffset;
    }

    public getYOffset(): number {
        return this.yOffset;
    }

    public stitchTilesByChangingElevation() {
        this._root.stitchTilesByChangingElevation();
    }

    public createReferencesToHeightMapTiles() {
        this._root.createReferencesToHeightMapTiles(null);
    }

    public completeChildren() {
        this._root.completeChildren();
    }

    public computeTesselatedAreas() {
        this._root.computeTesselatedAreas();
    }

    public getHeightForGeoLocation(aGeoLocation: TLatLng): number {
        let centerLevel = 21; // highest possible detail (should be determined somehow automatically according to the data provider like Bing, Google etc.)
        let proj = new WGS84Projection();
        let center = proj.lngLatToTileXY(aGeoLocation.lng, aGeoLocation.lat, centerLevel, false);

        let centerTile =
            this._root.getTileByAddress(
                new TerrainTileAddress(
                    Math.floor(center.x),
                    Math.floor(center.y),
                    centerLevel
                )
            );

        if (!centerTile.heightMapTile) {
            return 0;
        }

        center = proj.lngLatToTileXY(aGeoLocation.lng, aGeoLocation.lat, centerTile.heightMapTile.getDataTile().level.zoomLevel, false);

        let decpartX = center.x % 1;
        let decpartY = center.y % 1;

        let col = Math.floor(decpartX * (TerrainTile.TILE_SIZE.cols - 1));
        let row = Math.floor(decpartY * (TerrainTile.TILE_SIZE.rows - 1));

        let cx = decpartX;
        let cy = decpartY;

        let A = centerTile.heightMapTile.getHeightWithoutOffset(col, row);
        let B = centerTile.heightMapTile.getHeightWithoutOffset(col + 1, row);
        let C = centerTile.heightMapTile.getHeightWithoutOffset(col, row + 1);
        let D = centerTile.heightMapTile.getHeightWithoutOffset(col + 1, row + 1);

        let height = A * (1 - cx) * (1 - cy) + B * cx * (1 - cy) + C * (1 - cx) * cy + D * cx * cy;

        if (col > 0 && row > 0 && col < (TerrainTile.TILE_SIZE.cols - 1) && row < (TerrainTile.TILE_SIZE.rows - 1)) {
            centerTile.heightMapTile.setHeight(col, row, height);
        }

        if (col > 0 && row > 0 && col < (TerrainTile.TILE_SIZE.cols - 2) && row < (TerrainTile.TILE_SIZE.rows - 1)) {
            centerTile.heightMapTile.setHeight(col + 1, row, height);
        }

        if (col > 0 && row > 0 && col < (TerrainTile.TILE_SIZE.cols - 1) && row < (TerrainTile.TILE_SIZE.rows - 2)) {
            centerTile.heightMapTile.setHeight(col, row + 1, height);
        }

        if (col > 0 && row > 0 && col < (TerrainTile.TILE_SIZE.cols - 2) && row < (TerrainTile.TILE_SIZE.rows - 2)) {
            centerTile.heightMapTile.setHeight(col + 1, row + 1, height);
        }

        return height;
    }

    public getHeightMapTiles(): HeightMapTile[] {
        return this.heightMapTiles;
    }
}
